import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';

import loadingSvg from '../../assets/loading.svg';

import { ERRORS_TYPE } from '../../constants/errors';
import { routesPath } from '../../constants/routes';
import { useMutationUMALogin } from '../../graphql/mutation/umaLogin';
import { useLazyQueryClinic } from '../../graphql/query/getClinics';
import clsx from 'clsx';

export const LoginSSO = () => {
  const { loginWithRedirect, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const loginAttempted = useRef(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const { mutation: loginUser } = useMutationUMALogin({
    onCompleted: (res) => {
      if (res.createSessionWithUmaToken.token) {
        localStorage.setItem('token', res.createSessionWithUmaToken.token);
        getClinics();
      }
    },
    onError: (err: any) => {
      const errorMessage = err?.response?.data?.message || 'Authentication failed';
      setLoginError(errorMessage);
      // eslint-disable-next-line no-console
      console.error('UMA Login failed:', errorMessage);
    },
  });

  const { fetch: getClinics } = useLazyQueryClinic({
    onCompleted: ({ organizations }) => {
      goToDashboard(organizations);
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code;
      const extra = err?.response?.data?.extra;
      if (code === ERRORS_TYPE.FORCE_RESET_PASSWORD) {
        localStorage.setItem('resetToken', extra?.resetToken ?? '');
        history.push(routesPath.resetPassword);
      }
    },
  });
  const history = useHistory();

  useEffect(() => {
    (async () => {
      if (isAuthenticated && !loginAttempted.current) {
        loginAttempted.current = true;
        try {
          const token = await getAccessTokenSilently();
          // eslint-disable-next-line no-console
          console.log('Access token retrieved, sending to backend');
          loginUser({ variables: { umaToken: token } });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Error getting access token:', e);
          setLoginError('Failed to retrieve access token');
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const goToDashboard = (organizations: any) => {
    const hospitalRefId = localStorage.getItem('hospital-refId');

    if (!hospitalRefId) {
      const refId = organizations?.nodes?.[0]?.refId + '';
      localStorage.setItem('hospital-refId', refId);
      localStorage.removeItem('multiFilter-storage_null');
      history.push(`/${refId}/dashboard`);
    } else {
      history.push(`/${hospitalRefId}/dashboard`);
    }
  };
  return (
    <>
      <div className="flex flex-col items-center min-w-[25rem] h-full justify-center">
        <div className="text-4064">Welcome</div>
        {loginError && (
          <div className="w-full mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <p className="font-bold">Authentication Error</p>
            <p>{loginError}</p>
            <button
              onClick={() => {
                loginAttempted.current = false;
                setLoginError(null);
                loginWithRedirect();
              }}
              className="mt-2 text-sm underline"
            >
              Try again
            </button>
          </div>
        )}
        <Form className="w-full" onFinish={() => loginWithRedirect()} autoComplete="off">
          <button
            className={clsx(
              'text-white w-full rounded-main tracking-wider text-center text-1214 py-4 mt-14 flex justify-center',
              {
                'bg-persianBlue': isAuthenticated,
                'bg-black': !isAuthenticated,
              },
            )}
            type="submit"
            disabled={isAuthenticated && !loginError}
          >
            {isAuthenticated && !loginError ? (
              <img className="w-6" src={loadingSvg} alt="Loading..." />
            ) : (
              'Login with SSO'
            )}
          </button>
        </Form>
      </div>
      <div className="text-1020 opacity-70 font-[500] mb-8"></div>
    </>
  );
};
